import type { EventReminderInput } from '../types/event';

/** Egy eseményhez ennyi emlékeztető állítható be (a backend is ezt korlátozza). */
export const MAX_REMINDERS = 5;

export interface ReminderPreset {
  key: string;
  /** Rövid felirat a chipen. */
  label: string;
  reminder: EventReminderInput;
}

/**
 * Gyorsválasztók. A sorrend a legkorábbi értesítéstől a legkésőbbiig megy,
 * így a chipek sora magától időrendben olvasható.
 */
export const REMINDER_PRESETS: ReminderPreset[] = [
  { key: 'week', label: '1 héttel előtte', reminder: { offsetMinutes: 7 * 24 * 60 } },
  { key: 'days3', label: '3 nappal előtte', reminder: { offsetMinutes: 3 * 24 * 60 } },
  { key: 'day1', label: '1 nappal előtte', reminder: { offsetMinutes: 24 * 60 } },
  { key: 'eve', label: 'Előző nap 18:00', reminder: { offsetMinutes: 0, daysBefore: 1, timeOfDayMinutes: 18 * 60 } },
  { key: 'morning', label: 'Aznap reggel 8:00', reminder: { offsetMinutes: 0, daysBefore: 0, timeOfDayMinutes: 8 * 60 } },
  { key: 'hours3', label: '3 órával előtte', reminder: { offsetMinutes: 180 } },
  { key: 'hour1', label: '1 órával előtte', reminder: { offsetMinutes: 60 } },
];

export const isAbsoluteReminder = (reminder: EventReminderInput): boolean =>
  reminder.daysBefore != null && reminder.timeOfDayMinutes != null;

/** Egységes kulcs az összehasonlításhoz (chip kijelölés, duplikátumszűrés). */
export const reminderKey = (reminder: EventReminderInput): string =>
  isAbsoluteReminder(reminder)
    ? `abs:${reminder.daysBefore}:${reminder.timeOfDayMinutes}`
    : `rel:${reminder.offsetMinutes}`;

export const hasReminder = (reminders: EventReminderInput[], reminder: EventReminderInput): boolean =>
  reminders.some((r) => reminderKey(r) === reminderKey(reminder));

/** Ki-/bekapcsolja az adott emlékeztetőt a listában. */
export const toggleReminder = (
  reminders: EventReminderInput[],
  reminder: EventReminderInput
): EventReminderInput[] => {
  const key = reminderKey(reminder);
  return hasReminder(reminders, reminder)
    ? reminders.filter((r) => reminderKey(r) !== key)
    : [...reminders, reminder];
};

const formatTimeOfDay = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/** "45 perccel előtte", "3 órával előtte", "1 héttel előtte" */
export const formatOffset = (offsetMinutes: number): string => {
  if (offsetMinutes % (7 * 24 * 60) === 0) {
    const weeks = offsetMinutes / (7 * 24 * 60);
    return `${weeks} héttel előtte`;
  }
  if (offsetMinutes % (24 * 60) === 0) {
    const days = offsetMinutes / (24 * 60);
    return `${days} nappal előtte`;
  }
  if (offsetMinutes % 60 === 0) {
    return `${offsetMinutes / 60} órával előtte`;
  }
  return `${offsetMinutes} perccel előtte`;
};

/** Emberi leírás egy emlékeztetőhöz (a chipek és a nem-preset elemek feliratához). */
export const describeReminder = (reminder: EventReminderInput): string => {
  if (isAbsoluteReminder(reminder)) {
    const time = formatTimeOfDay(reminder.timeOfDayMinutes!);
    if (reminder.daysBefore === 0) return `Aznap ${time}`;
    if (reminder.daysBefore === 1) return `Előző nap ${time}`;
    return `${reminder.daysBefore} nappal korábban ${time}`;
  }

  return formatOffset(reminder.offsetMinutes);
};

/**
 * A tényleges küldési időpont - ugyanaz a számítás, mint a backendben
 * (EventReminder.CalculateScheduledAt), hogy az űrlapon látszódjon az eredmény.
 */
export const calculateScheduledAt = (
  eventStart: string,
  reminder: EventReminderInput
): Date | null => {
  const start = new Date(eventStart);
  if (Number.isNaN(start.getTime())) {
    return null;
  }

  if (isAbsoluteReminder(reminder)) {
    const scheduled = new Date(start);
    scheduled.setHours(0, 0, 0, 0);
    scheduled.setDate(scheduled.getDate() - reminder.daysBefore!);
    scheduled.setMinutes(reminder.timeOfDayMinutes!);
    return scheduled;
  }

  // Falióra-számítás (nem epoch-alapú), hogy a nyári/téli időszámítás váltásakor
  // is ugyanazt adja, mint a backend DateTime aritmetikája.
  const scheduled = new Date(start);
  scheduled.setMinutes(scheduled.getMinutes() - reminder.offsetMinutes);
  return scheduled;
};

/** "aug. 24. 08:00" */
export const formatScheduledAt = (date: Date): string =>
  date.toLocaleString('hu-HU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Időrendbe rendezi az emlékeztetőket (a legkorábban kimenő elöl). */
export const sortReminders = (
  reminders: EventReminderInput[],
  eventStart?: string | null
): EventReminderInput[] => {
  if (eventStart) {
    return [...reminders].sort((a, b) => {
      const aDate = calculateScheduledAt(eventStart, a)?.getTime() ?? 0;
      const bDate = calculateScheduledAt(eventStart, b)?.getTime() ?? 0;
      return aDate - bDate;
    });
  }

  // Kezdés nélkül közelítünk: az abszolút beállításokat az esemény napjának
  // végéhez viszonyítjuk (ugyanaz a súlyozás, mint a backendben).
  const weight = (r: EventReminderInput) =>
    isAbsoluteReminder(r)
      ? r.daysBefore! * 24 * 60 + (24 * 60 - r.timeOfDayMinutes!)
      : r.offsetMinutes;

  return [...reminders].sort((a, b) => weight(b) - weight(a));
};

/** Az egyéni időzítés mértékegységei. */
export const CUSTOM_UNITS = [
  { value: 'minutes', label: 'perccel', multiplier: 1 },
  { value: 'hours', label: 'órával', multiplier: 60 },
  { value: 'days', label: 'nappal', multiplier: 24 * 60 },
] as const;

export type CustomUnit = (typeof CUSTOM_UNITS)[number]['value'];
